import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fetchEndpoints, type Endpoint } from './controlApi';
import { fetchSites, type Site } from './fleetApi';
import { routePath } from './router';
import { ConfirmDialog } from './shared/ConfirmDialog';
import { FormDialog } from './shared/FormDialog';
import { PageHeader } from './shared/PageHeader';
import { ScheduleEditor } from './ScheduleEditor';
import { EditConflict, newSchedule, type DraftSchedule } from './scheduleDraft';
import {
  addMonths,
  deleteRecord,
  fetchBundleStatus,
  fetchOccurrences,
  fetchRecord,
  fetchRecords,
  fetchRevisions,
  fetchRuns,
  formatDateKey,
  formatDateTimeIn,
  formatTimeIn,
  listTimezones,
  monthGrid,
  monthLabel,
  OUTCOME_COLORS,
  OUTCOME_LABELS,
  paramText,
  restoreRevision,
  saveRecord,
  scheduleErrorText,
  todayIn,
  updateSiteLocation,
  WEEKDAYS,
  type BundleStatus,
  type HolidayData,
  type Occurrence,
  type Revision,
  type ScheduleData,
  type ScheduleRun,
  type StoredHoliday,
  type StoredSchedule,
} from './scheduleApi';

interface SchedulesProps {
  apiUrl: string;
  tenantId: string;
  siteId: string;
  token: string;
  permissions: string[];
  /** Tests pin "today". */
  now?: Date;
}

type Load =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      site: Site;
      schedules: StoredSchedule[];
      holidays: StoredHoliday[];
      endpoints: Endpoint[];
      status: BundleStatus;
    }
  | { kind: 'missing' }
  | { kind: 'error'; error: string };

type TabName = 'calendar' | 'schedules' | 'holidays' | 'history';

/**
 * A site's schedules (UI-003, SCH-008): a calendar of upcoming occurrences
 * (computed by the same code the appliance executes with), the schedules and
 * holidays themselves, run history reported by the appliance, and whether
 * each appliance has the latest schedules.
 */
export function Schedules({ apiUrl, tenantId, siteId, token, permissions, now }: SchedulesProps) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [tab, setTab] = useState<TabName>('calendar');
  const [editor, setEditor] = useState<{
    editing: StoredSchedule | null;
    initial: DraftSchedule;
  } | null>(null);
  const [locating, setLocating] = useState(false);
  const [version, setVersion] = useState(0); // bumps when schedules change
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  const canWrite = permissions.includes('schedule.write');
  const canSite = permissions.includes('site.write');

  const reload = useCallback(
    (signal?: AbortSignal) => {
      const t = tokenRef.current;
      Promise.all([
        fetchSites(apiUrl, t, tenantId, signal),
        fetchRecords<ScheduleData>(apiUrl, t, tenantId, siteId, 'schedules', signal),
        fetchRecords<HolidayData>(apiUrl, t, tenantId, siteId, 'holidays', signal),
        fetchEndpoints(apiUrl, t, tenantId, siteId, signal),
        fetchBundleStatus(apiUrl, t, tenantId, siteId, signal),
      ])
        .then(([sites, schedules, holidays, endpoints, status]) => {
          const site = sites.find((s) => s.id === siteId);
          if (!site) {
            setLoad({ kind: 'missing' });
            return;
          }
          setLoad({ kind: 'ready', site, schedules, holidays, endpoints, status });
          setVersion((v) => v + 1);
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          setLoad({ kind: 'error', error: scheduleErrorText(err) });
        });
    },
    [apiUrl, tenantId, siteId],
  );

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => {
      controller.abort();
    };
  }, [reload]);

  // The bundle reaches appliances within seconds: poll the sync state while
  // any appliance that is online lags behind.
  const lagging =
    load.kind === 'ready' && load.status.appliances.some((a) => a.online && !a.in_sync);
  useEffect(() => {
    if (!lagging) return;
    const id = setInterval(() => {
      fetchBundleStatus(apiUrl, tokenRef.current, tenantId, siteId)
        .then((status) => {
          setLoad((prev) => (prev.kind === 'ready' ? { ...prev, status } : prev));
        })
        .catch(() => undefined);
    }, 3000);
    return () => {
      clearInterval(id);
    };
  }, [lagging, apiUrl, tenantId, siteId]);

  if (load.kind === 'loading')
    return <Typography color="text.secondary">Loading schedules…</Typography>;
  if (load.kind === 'missing') {
    return (
      <Alert severity="warning">This site doesn&apos;t exist or isn&apos;t in your access.</Alert>
    );
  }
  if (load.kind === 'error')
    return <Alert severity="error">Could not load schedules: {load.error}</Alert>;

  const { site, schedules, holidays, endpoints, status } = load;
  const tz = site.timezone;
  const hasLocation = site.latitude != null && site.longitude != null;
  const today = todayIn(tz, now);
  const writable = canWrite && status.enabled;

  const openNew = (date: string) => {
    setEditor({ editing: null, initial: newSchedule(date, endpoints) });
  };
  const openEdit = (s: StoredSchedule) => {
    setEditor({ editing: s, initial: structuredClone(s.data) });
  };
  const saveSchedule = async (data: DraftSchedule, force: boolean) => {
    const t = tokenRef.current;
    const editing = editor?.editing;
    if (editing && !force) {
      // Last write wins, but say so before overwriting someone (SCH-013).
      const cur = await fetchRecord<ScheduleData>(
        apiUrl,
        t,
        tenantId,
        siteId,
        'schedules',
        editing.id,
      );
      if (cur.rev !== editing.rev) throw new EditConflict(cur.modified_by);
    }
    await saveRecord(
      apiUrl,
      t,
      tenantId,
      siteId,
      'schedules',
      data,
      editing ? { id: editing.id, baseRev: editing.rev } : undefined,
    );
    reload();
  };

  return (
    <Stack spacing={2} data-testid="schedules-page">
      <PageHeader
        title="Schedules"
        crumbs={[
          { label: 'Sites', path: '/' },
          { label: site.name, path: routePath({ name: 'site', siteId }) },
        ]}
        subtitle={
          <span data-testid="site-location">
            {tz} ·{' '}
            {hasLocation
              ? `${String(site.latitude?.toFixed(4))}, ${String(site.longitude?.toFixed(4))}`
              : 'no location (needed for sunrise/sunset)'}
            {canSite && (
              <Button
                size="small"
                sx={{ ml: 1 }}
                data-testid="site-location-edit"
                onClick={() => {
                  setLocating(true);
                }}
              >
                Change
              </Button>
            )}
          </span>
        }
        actions={
          <>
            <SyncChip status={status} />
            {writable && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                data-testid="schedule-new"
                disabled={endpoints.length === 0}
                onClick={() => {
                  openNew(today);
                }}
              >
                New schedule
              </Button>
            )}
          </>
        }
      />

      {!status.enabled && (
        <Alert severity="warning">
          Scheduling isn&apos;t configured on this server, so schedules can&apos;t be changed.
        </Alert>
      )}
      {status.appliances.length === 0 && (
        <Alert severity="info">
          No appliance is installed at this site yet: schedules will run once one is.
        </Alert>
      )}
      <SyncDetail status={status} />

      <Tabs
        value={tab}
        onChange={(_, v: TabName) => {
          setTab(v);
        }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab value="calendar" label="Calendar" data-testid="tab-calendar" />
        <Tab
          value="schedules"
          label={`Schedules (${String(schedules.length)})`}
          data-testid="tab-schedules"
        />
        <Tab
          value="holidays"
          label={`Holidays (${String(holidays.length)})`}
          data-testid="tab-holidays"
        />
        <Tab value="history" label="Run history" data-testid="tab-history" />
      </Tabs>

      {tab === 'calendar' && (
        <CalendarView
          apiUrl={apiUrl}
          tenantId={tenantId}
          siteId={siteId}
          tokenRef={tokenRef}
          timezone={tz}
          today={today}
          version={version}
          onDay={writable && endpoints.length > 0 ? openNew : undefined}
          onOccurrence={(o) => {
            const s = schedules.find((x) => x.id === o.schedule_id);
            if (s && writable) openEdit(s);
          }}
        />
      )}
      {tab === 'schedules' && (
        <ScheduleList
          schedules={schedules}
          endpoints={endpoints}
          writable={writable}
          onEdit={openEdit}
          onToggle={async (s, enabled) => {
            await saveRecord(
              apiUrl,
              tokenRef.current,
              tenantId,
              siteId,
              'schedules',
              { ...s.data, enabled },
              {
                id: s.id,
                baseRev: s.rev,
              },
            );
            reload();
          }}
          onDelete={async (s) => {
            await deleteRecord(
              apiUrl,
              tokenRef.current,
              tenantId,
              siteId,
              'schedules',
              s.id,
              s.rev,
            );
            reload();
          }}
          loadRevisions={(s) =>
            fetchRevisions<ScheduleData>(
              apiUrl,
              tokenRef.current,
              tenantId,
              siteId,
              'schedules',
              s.id,
            )
          }
          onRestore={async (s, rev) => {
            await restoreRevision(
              apiUrl,
              tokenRef.current,
              tenantId,
              siteId,
              'schedules',
              s.id,
              rev,
              s.rev,
            );
            reload();
          }}
        />
      )}
      {tab === 'holidays' && (
        <HolidayList
          holidays={holidays}
          writable={writable}
          onAdd={async (h) => {
            await saveRecord(apiUrl, tokenRef.current, tenantId, siteId, 'holidays', h);
            reload();
          }}
          onDelete={async (h) => {
            await deleteRecord(apiUrl, tokenRef.current, tenantId, siteId, 'holidays', h.id, h.rev);
            reload();
          }}
        />
      )}
      {tab === 'history' && (
        <RunHistory
          load={(signal) =>
            fetchRuns(apiUrl, tokenRef.current, tenantId, siteId, { limit: 200 }, signal)
          }
          schedules={schedules}
          endpoints={endpoints}
          timezone={tz}
        />
      )}

      <ScheduleEditor
        open={editor !== null}
        editing={editor?.editing ?? null}
        initial={editor?.initial ?? newSchedule(today, endpoints)}
        timezone={tz}
        hasLocation={hasLocation}
        endpoints={endpoints}
        onClose={() => {
          setEditor(null);
        }}
        onSave={saveSchedule}
      />
      {canSite && (
        <LocationDialog
          open={locating}
          site={site}
          onClose={() => {
            setLocating(false);
          }}
          onSave={async (loc) => {
            await updateSiteLocation(apiUrl, tokenRef.current, tenantId, siteId, loc);
            reload();
          }}
        />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------- sync status

function SyncChip({ status }: { status: BundleStatus }) {
  if (status.appliances.length === 0 || !status.latest) return null;
  const behind = status.appliances.filter((a) => !a.in_sync).length;
  return (
    <Chip
      size="small"
      data-testid="sync-status"
      color={behind === 0 ? 'success' : 'warning'}
      label={
        behind === 0
          ? `In effect on site (v${String(status.latest.version)})`
          : `Update pending on ${String(behind)} appliance${behind === 1 ? '' : 's'}`
      }
    />
  );
}

function SyncDetail({ status }: { status: BundleStatus }) {
  const behind = status.appliances.filter((a) => !a.in_sync);
  if (!status.latest || behind.length === 0) return null;
  return (
    <Alert severity="info" data-testid="sync-detail">
      Latest schedules: v{status.latest.version}.{' '}
      {behind
        .map(
          (a) =>
            `${a.name ?? a.serial} runs v${String(a.version)}${a.online ? ' (updating…)' : ' (offline: it updates when it reconnects, and keeps running v' + String(a.version) + ' meanwhile)'}`,
        )
        .join('; ')}
    </Alert>
  );
}

// ---------------------------------------------------------------- calendar

function CalendarView({
  apiUrl,
  tenantId,
  siteId,
  tokenRef,
  timezone,
  today,
  version,
  onDay,
  onOccurrence,
}: {
  apiUrl: string;
  tenantId: string;
  siteId: string;
  tokenRef: React.RefObject<string>;
  timezone: string;
  today: string;
  version: number;
  onDay?: (date: string) => void;
  onOccurrence: (o: Occurrence) => void;
}) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [month, setMonth] = useState(`${today.slice(0, 7)}-01`);
  const grid = useMemo(() => monthGrid(month), [month]);
  const [occ, setOcc] = useState<{ key: string; list: Occurrence[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = `${month}/${String(version)}`;

  useEffect(() => {
    const controller = new AbortController();
    fetchOccurrences(
      apiUrl,
      tokenRef.current,
      tenantId,
      siteId,
      grid[0] ?? month,
      grid[41] ?? month,
      controller.signal,
    )
      .then((list) => {
        setOcc({ key, list });
        setError(null);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(scheduleErrorText(err));
      });
    return () => {
      controller.abort();
    };
  }, [apiUrl, tenantId, siteId, tokenRef, grid, month, key]);

  const byDate = useMemo(() => {
    const m = new Map<string, Occurrence[]>();
    for (const o of occ?.list ?? []) {
      const l = m.get(o.date) ?? [];
      l.push(o);
      m.set(o.date, l);
    }
    return m;
  }, [occ]);

  const header = (
    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
      <IconButton
        aria-label="Previous month"
        onClick={() => {
          setMonth(addMonths(month, -1));
        }}
      >
        <ChevronLeftIcon />
      </IconButton>
      <Typography variant="h6" component="h2" data-testid="calendar-month">
        {monthLabel(month)}
      </Typography>
      <IconButton
        aria-label="Next month"
        onClick={() => {
          setMonth(addMonths(month, 1));
        }}
      >
        <ChevronRightIcon />
      </IconButton>
    </Stack>
  );
  const loading = occ?.key !== key;

  const event = (o: Occurrence) => (
    <Box
      key={`${o.schedule_id}/${o.date}`}
      role="button"
      tabIndex={0}
      data-testid="calendar-event"
      onClick={(e) => {
        e.stopPropagation();
        onOccurrence(o);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOccurrence(o);
      }}
      sx={{
        fontSize: '0.75rem',
        lineHeight: 1.3,
        px: 0.5,
        borderRadius: 0.5,
        mb: 0.25,
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        bgcolor: o.outcome
          ? `${OUTCOME_COLORS[o.outcome] === 'default' ? 'grey' : OUTCOME_COLORS[o.outcome]}.light`
          : 'action.hover',
        textDecoration: o.holiday_skip || !o.enabled ? 'line-through' : 'none',
        opacity: o.holiday_skip || !o.enabled ? 0.6 : 1,
      }}
      title={`${o.name}${o.holiday_skip ? ' (skipped: holiday)' : ''}${o.enabled ? '' : ' (disabled)'}${o.outcome ? ` — ${OUTCOME_LABELS[o.outcome]}` : ''}`}
    >
      {o.at ? formatTimeIn(o.at, timezone) : '—'} {o.name}
    </Box>
  );

  if (phone) {
    // Phones get an agenda: only the days with something on them.
    const days = grid.filter(
      (d) => d.slice(0, 7) === month.slice(0, 7) && (byDate.get(d)?.length ?? 0) > 0,
    );
    return (
      <Stack spacing={1} data-testid="calendar">
        {header}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && days.length === 0 && (
          <Typography color="text.secondary">Nothing scheduled this month.</Typography>
        )}
        {days.map((d) => (
          <Card key={d} variant="outlined">
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Typography variant="subtitle2" color={d === today ? 'primary' : 'text.primary'}>
                {WEEKDAYS[new Date(`${d}T00:00:00Z`).getUTCDay()]} {formatDateKey(d)}
              </Typography>
              {(byDate.get(d) ?? []).map(event)}
            </CardContent>
          </Card>
        ))}
        {onDay && (
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              onDay(month.slice(0, 7) === today.slice(0, 7) ? today : month);
            }}
          >
            Add on a day of this month
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Box data-testid="calendar">
      {header}
      {error && <Alert severity="error">{error}</Alert>}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          opacity: loading ? 0.6 : 1,
        }}
      >
        {WEEKDAYS.map((w) => (
          <Box key={w} sx={{ p: 0.5, textAlign: 'center', bgcolor: 'action.hover' }}>
            <Typography variant="caption">{w}</Typography>
          </Box>
        ))}
        {grid.map((d) => {
          const inMonth = d.slice(0, 7) === month.slice(0, 7);
          return (
            <Box
              key={d}
              data-testid={`calendar-day-${d}`}
              onClick={
                onDay
                  ? () => {
                      onDay(d);
                    }
                  : undefined
              }
              sx={{
                minHeight: 92,
                p: 0.5,
                borderTop: 1,
                borderColor: 'divider',
                cursor: onDay ? 'pointer' : 'default',
                bgcolor: inMonth ? 'background.paper' : 'action.disabledBackground',
                '&:hover': onDay ? { bgcolor: 'action.selected' } : undefined,
                minWidth: 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: d === today ? 700 : 400,
                  color: d === today ? 'primary.main' : 'text.secondary',
                }}
              >
                {Number(d.slice(8, 10))}
              </Typography>
              {(byDate.get(d) ?? []).map(event)}
            </Box>
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Times are in {timezone}. Struck-through: skipped for a holiday, or disabled.
        {onDay ? ' Click a day to add a schedule.' : ''}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------- schedules

function actionSummary(s: StoredSchedule, endpoints: Endpoint[]): string {
  return s.data.actions
    .map((a) => {
      const ep = endpoints.find((e) => e.id === a.endpoint_id);
      const target = Object.values(a.params as Record<string, unknown>)[0];
      return `${a.action}${target === undefined ? '' : ` ${paramText(target)}`} on ${ep?.name ?? 'a removed endpoint'}`;
    })
    .join(', ');
}

function ScheduleList({
  schedules,
  endpoints,
  writable,
  onEdit,
  onToggle,
  onDelete,
  loadRevisions,
  onRestore,
}: {
  schedules: StoredSchedule[];
  endpoints: Endpoint[];
  writable: boolean;
  onEdit: (s: StoredSchedule) => void;
  onToggle: (s: StoredSchedule, enabled: boolean) => Promise<void>;
  onDelete: (s: StoredSchedule) => Promise<void>;
  loadRevisions: (s: StoredSchedule) => Promise<Revision<ScheduleData>[]>;
  onRestore: (s: StoredSchedule, rev: number) => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<StoredSchedule | null>(null);
  const [deleting, setDeleting] = useState<StoredSchedule | null>(null);
  const guard = (p: Promise<void>) => {
    setError(null);
    p.catch((err: unknown) => {
      setError(scheduleErrorText(err));
    });
  };
  if (schedules.length === 0) {
    return <Typography color="text.secondary">No schedules yet.</Typography>;
  }
  return (
    <Stack spacing={1.5}>
      {error && <Alert severity="error">{error}</Alert>}
      {schedules.map((s) => (
        <Card key={s.id} variant="outlined" data-testid="schedule-card">
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-start' } }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ overflowWrap: 'anywhere' }}>
                  {s.data.name}
                </Typography>
                <Typography variant="body2">{s.summary}</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  {actionSummary(s, endpoints)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Revision {s.rev} by {s.modified_by}
                  {s.origin === 'appliance' ? ' (on site)' : ''}
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={s.data.enabled}
                      disabled={!writable}
                      onChange={(e) => {
                        guard(onToggle(s, e.target.checked));
                      }}
                    />
                  }
                  label={s.data.enabled ? 'On' : 'Off'}
                />
                <Button
                  size="small"
                  onClick={() => {
                    setHistory(s);
                  }}
                >
                  History
                </Button>
                {writable && (
                  <>
                    <Button
                      size="small"
                      onClick={() => {
                        onEdit(s);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setDeleting(s);
                      }}
                    >
                      Delete
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}
      <ConfirmDialog
        open={deleting !== null}
        title={`Delete “${deleting?.data.name ?? ''}”?`}
        confirmLabel="Delete"
        confirmTestId="schedule-delete-confirm"
        describeError={scheduleErrorText}
        onClose={() => {
          setDeleting(null);
        }}
        onConfirm={async () => {
          if (deleting) await onDelete(deleting);
        }}
      >
        It stops running on the appliance. It stays in the history and can be restored.
      </ConfirmDialog>
      {history && (
        <RevisionsDialog
          schedule={history}
          writable={writable}
          load={loadRevisions}
          onRestore={async (rev) => {
            await onRestore(history, rev);
            setHistory(null);
          }}
          onClose={() => {
            setHistory(null);
          }}
        />
      )}
    </Stack>
  );
}

function RevisionsDialog({
  schedule,
  writable,
  load,
  onRestore,
  onClose,
}: {
  schedule: StoredSchedule;
  writable: boolean;
  load: (s: StoredSchedule) => Promise<Revision<ScheduleData>[]>;
  onRestore: (rev: number) => Promise<void>;
  onClose: () => void;
}) {
  const [revs, setRevs] = useState<Revision<ScheduleData>[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    load(schedule)
      .then(setRevs)
      .catch((err: unknown) => {
        setError(scheduleErrorText(err));
      });
  }, [load, schedule]);
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>History of &ldquo;{schedule.data.name}&rdquo;</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!revs && !error && <Typography color="text.secondary">Loading…</Typography>}
        <Stack spacing={1} data-testid="revisions">
          {revs?.map((r) => (
            <Card key={r.rev} variant="outlined">
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                      Revision {r.rev}: {r.deleted ? 'deleted' : r.data?.name} — {r.modified_by}
                      {r.origin === 'appliance' ? ' (on site)' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(r.modified_at).toLocaleString()}
                    </Typography>
                    {r.overwritten_by && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                        Overwritten by {r.overwritten_by}, who hadn&apos;t seen it
                      </Typography>
                    )}
                  </Box>
                  {writable && !r.deleted && r.rev !== schedule.rev && (
                    <Button
                      size="small"
                      onClick={() => {
                        onRestore(r.rev).catch((err: unknown) => {
                          setError(scheduleErrorText(err));
                        });
                      }}
                    >
                      Restore
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------- holidays

function HolidayList({
  holidays,
  writable,
  onAdd,
  onDelete,
}: {
  holidays: StoredHoliday[];
  writable: boolean;
  onAdd: (h: HolidayData) => Promise<void>;
  onDelete: (h: StoredHoliday) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [yearly, setYearly] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const sorted = [...holidays].sort((a, b) => a.data.date.localeCompare(b.data.date));
  return (
    <Stack spacing={1.5}>
      {writable && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ alignItems: { sm: 'center' } }}
        >
          <TextField
            size="small"
            label="Holiday"
            value={name}
            slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'holiday-name' } }}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <TextField
            size="small"
            label="Date"
            type="date"
            value={date}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { 'data-testid': 'holiday-date' },
            }}
            onChange={(e) => {
              setDate(e.target.value);
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={yearly}
                onChange={(e) => {
                  setYearly(e.target.checked);
                }}
              />
            }
            label="Every year"
          />
          <Button
            variant="outlined"
            data-testid="holiday-add"
            disabled={!name.trim() || !date}
            onClick={() => {
              setError(null);
              onAdd({ name: name.trim(), date, recurring_yearly: yearly })
                .then(() => {
                  setName('');
                  setDate('');
                })
                .catch((err: unknown) => {
                  setError(scheduleErrorText(err));
                });
            }}
          >
            Add holiday
          </Button>
        </Stack>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {sorted.length === 0 && <Typography color="text.secondary">No holidays.</Typography>}
      {sorted.map((h) => (
        <Stack
          key={h.id}
          direction="row"
          spacing={1}
          data-testid="holiday-row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            py: 0.5,
          }}
        >
          <Typography sx={{ overflowWrap: 'anywhere' }}>
            {h.data.name} —{' '}
            {h.data.recurring_yearly
              ? `every ${formatDateKey(h.data.date).replace(/, \d+$/, '')}`
              : formatDateKey(h.data.date)}
          </Typography>
          {writable && (
            <Button
              size="small"
              color="error"
              onClick={() => {
                onDelete(h).catch((err: unknown) => {
                  setError(scheduleErrorText(err));
                });
              }}
            >
              Remove
            </Button>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

// ---------------------------------------------------------------- run history

function RunHistory({
  load,
  schedules,
  endpoints,
  timezone,
}: {
  load: (signal: AbortSignal) => Promise<ScheduleRun[]>;
  schedules: StoredSchedule[];
  endpoints: Endpoint[];
  timezone: string;
}) {
  const [runs, setRuns] = useState<ScheduleRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal)
      .then(setRuns)
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(scheduleErrorText(err));
      });
    return () => {
      controller.abort();
    };
  }, [load]);
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!runs) return <Typography color="text.secondary">Loading…</Typography>;
  if (runs.length === 0) {
    return (
      <Typography color="text.secondary">
        No runs reported yet. Runs appear here once the appliance reports them; while it is offline
        it keeps running schedules and reports them when it reconnects.
      </Typography>
    );
  }
  const name = (id: string) => schedules.find((s) => s.id === id)?.data.name ?? 'Deleted schedule';
  const epName = (id: string) => endpoints.find((e) => e.id === id)?.name ?? 'endpoint';
  return (
    <Stack spacing={1} data-testid="run-history">
      {runs.map((r) => {
        const lateMin =
          r.fired_at !== null
            ? Math.round(
                (new Date(r.fired_at).getTime() - new Date(r.scheduled_for).getTime()) / 60000,
              )
            : 0;
        const failures = r.actions.filter((a) => a.status === 'error');
        return (
          <Stack
            key={r.run_id}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            data-testid="run-row"
            sx={{ borderBottom: 1, borderColor: 'divider', py: 0.75, alignItems: { sm: 'center' } }}
          >
            <Typography variant="body2" sx={{ minWidth: 150 }}>
              {formatDateTimeIn(r.scheduled_for, timezone)}
            </Typography>
            <Typography variant="body2" sx={{ flexGrow: 1, overflowWrap: 'anywhere' }}>
              {name(r.schedule_id)}
              {lateMin >= 2 ? ` · ran ${String(lateMin)} min late` : ''}
              {failures.length > 0 &&
                ` · ${failures.map((a) => `${epName(a.endpoint_id)}: ${a.detail ?? a.code ?? 'error'}`).join('; ')}`}
            </Typography>
            <Chip
              size="small"
              label={OUTCOME_LABELS[r.outcome]}
              color={OUTCOME_COLORS[r.outcome]}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
            />
          </Stack>
        );
      })}
    </Stack>
  );
}

// ---------------------------------------------------------------- location

function LocationDialog({
  open,
  site,
  onClose,
  onSave,
}: {
  open: boolean;
  site: Site;
  onClose: () => void;
  onSave: (loc: {
    timezone: string;
    latitude: number | null;
    longitude: number | null;
  }) => Promise<void>;
}) {
  const [tz, setTz] = useState(site.timezone);
  const [lat, setLat] = useState(site.latitude == null ? '' : String(site.latitude));
  const [lon, setLon] = useState(site.longitude == null ? '' : String(site.longitude));
  const zones = listTimezones();
  const both = lat.trim() !== '' && lon.trim() !== '';
  const neither = lat.trim() === '' && lon.trim() === '';
  const valid =
    zones.includes(tz) &&
    (neither ||
      (both &&
        Math.abs(Number(lat)) <= 90 &&
        Math.abs(Number(lon)) <= 180 &&
        !isNaN(Number(lat)) &&
        !isNaN(Number(lon))));
  return (
    <FormDialog
      open={open}
      title="Change time zone and location"
      submitLabel="Save"
      maxWidth="xs"
      canSubmit={valid}
      submitTestId="site-location-save"
      describeError={scheduleErrorText}
      onClose={onClose}
      onSubmit={async () => {
        await onSave({
          timezone: tz,
          latitude: neither ? null : Number(lat),
          longitude: neither ? null : Number(lon),
        });
        onClose();
      }}
    >
      <TextField
        select
        label="Time zone"
        value={zones.includes(tz) ? tz : ''}
        onChange={(e) => {
          setTz(e.target.value);
        }}
      >
        {zones.map((z) => (
          <MenuItem key={z} value={z}>
            {z}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction="row" spacing={2}>
        <TextField
          label="Latitude"
          value={lat}
          slotProps={{ htmlInput: { inputMode: 'decimal', 'data-testid': 'site-lat' } }}
          onChange={(e) => {
            setLat(e.target.value);
          }}
        />
        <TextField
          label="Longitude"
          value={lon}
          slotProps={{ htmlInput: { inputMode: 'decimal', 'data-testid': 'site-lon' } }}
          onChange={(e) => {
            setLon(e.target.value);
          }}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Sunrise and sunset schedules need the location. Changing either updates every
        schedule&apos;s times on the appliance.
      </Typography>
    </FormDialog>
  );
}
